/**
 * PipelineActivityTrailView — the audit-trail row list plus its loading, error,
 * empty and load-more states (SE-5504).
 *
 * Shared by the card-scoped trail in the player preview drawer and the
 * whole-pipeline manager log, so both surfaces present an audit trail
 * identically and only the SCOPE differs.
 *
 * Renders `rows` in the order it receives them — the caller has already ordered
 * them newest-first, and nothing here drops, hides or re-orders an entry. That
 * is a hard requirement of the ticket, not a stylistic choice: the surface must
 * expose a backend gap rather than mask it.
 *
 * There is deliberately NO filter-chip row. AC3's chips are real SERVER-SIDE
 * filters and `pipelineActivityLog` accepts no filter arguments (SE-6248
 * tracks adding them). A decorative or client-side chip row was explicitly
 * ruled out, so the chips attach here — above the list — once the query can
 * actually honour them.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/PipelineActivityTrail
 */

import { memo } from "react";

import { TEST_ID_PIPELINE_ACTIVITY } from "e2e/testId";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import PipelineActivityRow from "../PipelineActivityRow";

import type { PipelineActivityRow as PipelineActivityRowModel } from "../../../utils/pipelineActivityRows";

/** Props for {@link PipelineActivityTrailView}. */
export interface PipelineActivityTrailViewProps {
  /** Rows to render, already ordered newest-first by the caller. */
  readonly rows: readonly PipelineActivityRowModel[];
  /** Whether the first page is in flight. */
  readonly loading: boolean;
  /** Whether the read failed. */
  readonly isError: boolean;
  /** Whether the server says more pages exist. */
  readonly hasMore: boolean;
  /** Whether a "Load more" page is in flight. */
  readonly isLoadingMore: boolean;
  /** Fetch the next page. */
  readonly onLoadMore: () => void;
  /** Copy for the zero-entry state. */
  readonly emptyLabel: string;
  /** Whether each row prints the player's name (board-wide log does). */
  readonly showPlayerName?: boolean;
  /** Test identity for the list root. */
  readonly testID?: string;
}

/**
 * Render the spinner / error / empty state, or `null` when there are rows to
 * show. Module-scope because the View itself must be an arrow-shorthand
 * component with no statement body (`component-structure/no-return-in-view`).
 * @param props - The full view props.
 * @returns The state node, or `null` when the row list should render.
 */
const renderState = (props: PipelineActivityTrailViewProps) => {
  if (props.loading && props.rows.length === 0) {
    return (
      <Box
        testID={TEST_ID_PIPELINE_ACTIVITY.LOADING}
        className="items-center justify-center py-6"
      >
        <Spinner />
      </Box>
    );
  }

  if (props.isError) {
    return (
      <Box testID={TEST_ID_PIPELINE_ACTIVITY.ERROR}>
        <Text variant="body" className="text-status-error">
          Could not load the activity log. Please try again.
        </Text>
      </Box>
    );
  }

  if (props.rows.length === 0) {
    return (
      <Box testID={TEST_ID_PIPELINE_ACTIVITY.EMPTY}>
        <Text variant="body" className="text-content-muted">
          {props.emptyLabel}
        </Text>
      </Box>
    );
  }

  return null;
};

/**
 * Render the row list. Reached only once {@link renderState} has nothing to
 * show, i.e. there is at least one entry.
 * @param props - The full view props.
 * @returns The row list.
 */
const renderRows = (props: PipelineActivityTrailViewProps) => (
  // Entries no longer carry their own card, so the gap between them is what
  // separates one from the next — a tighter step than the bordered rows it
  // replaced would read as a single run-on block.
  <Stack space="3" testID={props.testID}>
    {props.rows.map(row => (
      <PipelineActivityRow
        key={row.id}
        row={row}
        showPlayerName={props.showPlayerName ?? false}
      />
    ))}
    {props.hasMore ? (
      <Button
        testID={TEST_ID_PIPELINE_ACTIVITY.LOAD_MORE}
        label="Load more"
        accessibilityLabel="Load more activity"
        variant="outline"
        tone="secondary"
        size="sm"
        isDisabled={props.isLoadingMore}
        isLoading={props.isLoadingMore}
        onPress={props.onLoadMore}
      />
    ) : null}
  </Stack>
);

/**
 * Render the audit-trail list.
 * @param props - {@link PipelineActivityTrailViewProps}
 * @returns The trail.
 */
const PipelineActivityTrailView = (props: PipelineActivityTrailViewProps) => (
  <>{renderState(props) ?? renderRows(props)}</>
);

PipelineActivityTrailView.displayName = "PipelineActivityTrailView";

export default memo(PipelineActivityTrailView);
